import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo, useEffect } from "react";
import { PageShell } from "@/components/site/page-shell";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Minus, Search } from "lucide-react";

export const Route = createFileRoute("/faq")({
  component: FaqPage,
  head: () => ({
    meta: [
      { title: "FAQ — CoderLity" },
      { name: "description", content: "Answers to the most common questions about our services, process and pricing." },
      { property: "og:title", content: "FAQ — CoderLity" },
      { property: "og:description", content: "Answers to the most common questions about our services, process and pricing." },
      { property: "og:url", content: "/faq" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/faq" }],
  }),
});

type Faq = { id: string; question: string; answer: string; category: string | null; sort_order: number };

function FaqPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["public", "faqs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("faqs")
        .select("id,question,answer,category,sort_order")
        .eq("published", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data as Faq[];
    },
  });

  const [openId, setOpenId] = useState<string | null>(null);
  const [activeCat, setActiveCat] = useState<string>("all");
  const [query, setQuery] = useState("");

  const categories = useMemo(() => {
    const set = new Set<string>();
    (data ?? []).forEach((f) => f.category && set.add(f.category));
    return Array.from(set);
  }, [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data ?? []).filter((f) => {
      if (activeCat !== "all" && f.category !== activeCat) return false;
      if (!q) return true;
      return (
        f.question.toLowerCase().includes(q) ||
        f.answer.toLowerCase().includes(q) ||
        (f.category ?? "").toLowerCase().includes(q)
      );
    });
  }, [data, activeCat, query]);

  // Auto-open first item when list changes
  useEffect(() => {
    if (filtered.length && !filtered.some((f) => f.id === openId)) {
      setOpenId(filtered[0].id);
    }
  }, [filtered, openId]);

  const jsonLd = data?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: data.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      }
    : null;

  return (
    <PageShell>
      <section className="relative overflow-hidden bg-[hsl(222_47%_11%)] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,hsl(217_91%_60%/0.15),transparent_50%),radial-gradient(circle_at_80%_80%,hsl(217_91%_60%/0.1),transparent_50%)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16 items-start">
            {/* Left column */}
            <div className="relative">
              <h1 className="text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
                Freequently<br />asked Questions
              </h1>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60">
                Have questions about our services, process, or pricing? Our FAQ section is here to provide clear answers and help you better understand how CoderLity can support your digital journey.
              </p>
              <div className="mt-8 relative inline-block">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition hover:brightness-110"
                >
                  Let's Talk
                </Link>
                {/* Dashed arrow decoration */}
                <svg
                  aria-hidden="true"
                  className="pointer-events-none absolute left-24 top-10 hidden h-32 w-48 text-primary/70 sm:block"
                  viewBox="0 0 200 140"
                  fill="none"
                >
                  <path
                    d="M10 10 C 60 40, 20 90, 80 100 S 170 80, 180 120"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeDasharray="5 6"
                    strokeLinecap="round"
                  />
                  <path
                    d="M170 108 L182 122 L166 126"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </div>
            </div>

            {/* Right column */}
            <div>
              <div className="relative mb-4">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search questions…"
                  className="w-full rounded-full border border-white/10 bg-white/[0.06] py-3 pl-11 pr-4 text-sm text-white placeholder:text-white/40 outline-none transition focus:border-primary/60 focus:bg-white/[0.09]"
                  aria-label="Search FAQs"
                />
              </div>

              {categories.length > 0 && (
                <div className="mb-5 flex flex-wrap gap-2">
                  <button
                    onClick={() => setActiveCat("all")}
                    className={`rounded-full border px-3 py-1 text-xs transition ${
                      activeCat === "all"
                        ? "border-primary bg-primary/20 text-white"
                        : "border-white/15 text-white/70 hover:border-white/40"
                    }`}
                  >
                    All
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c}
                      onClick={() => setActiveCat(c)}
                      className={`rounded-full border px-3 py-1 text-xs transition ${
                        activeCat === c
                          ? "border-primary bg-primary/20 text-white"
                          : "border-white/15 text-white/70 hover:border-white/40"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}

              {isLoading && <p className="text-center text-white/60">Loading…</p>}
              {!isLoading && !filtered.length && (
                <p className="text-center text-white/60">No questions match your search.</p>
              )}

              <div className="space-y-3">
                {filtered.map((f) => {
                  const isOpen = openId === f.id;
                  return (
                    <div
                      key={f.id}
                      className={`overflow-hidden rounded-2xl transition-all duration-300 ${
                        isOpen
                          ? "bg-primary text-primary-foreground shadow-xl shadow-primary/30"
                          : "bg-white/[0.06] text-white hover:bg-white/[0.09]"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setOpenId(isOpen ? null : f.id)}
                        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                        aria-expanded={isOpen}
                      >
                        <span className="text-sm font-semibold sm:text-[15px]">{f.question}</span>
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition ${
                            isOpen ? "bg-white/20" : "bg-white/10"
                          }`}
                        >
                          {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                        </span>
                      </button>
                      <div
                        className={`grid transition-all duration-300 ease-out ${
                          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <p className="px-5 pb-5 text-sm leading-relaxed text-primary-foreground/90 whitespace-pre-wrap">
                            {f.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
      {jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      )}
    </PageShell>
  );
}
